import { RemoveIcon, SuccessIcon } from "@hopper-ui/icons";
import { StyledSystemProvider, useColorSchemeContext } from "@hopper-ui/styled-system";
import { createPortal } from "react-dom";
import { ToastContainer as ToastifyToastContainer } from "react-toastify";

interface ToastContainerProps {
    /**
     * Render the toasts through a portal to `document.body` so they escape the
     * stacking context of any ancestor (e.g. the host side panel). Because the
     * portal target lives outside the `HopperProvider` subtree where the
     * `--hop-*` design tokens are defined, the portaled toasts are re-wrapped in
     * a `StyledSystemProvider` so the tokens (and color scheme) still resolve.
     * Defaults to `false`.
     */
    portal?: boolean;
}

export const ToastContainer = ({ portal = false }: ToastContainerProps) => {
    const { colorScheme } = useColorSchemeContext();

    const toasts = (
        <ToastifyToastContainer
            theme={colorScheme}
            className="wai-toasts"
            style={{ zIndex: 1000060 }}
            icon={({ type }) => {
                if (type === "success") {
                    return <SuccessIcon fill="success-weakest" />;
                }
                if (type === "error") {
                    return <RemoveIcon fill="danger-weak" />;
                }
            }}
        />
    );

    if (!portal || typeof document === "undefined" || !document.body) {
        return toasts;
    }

    return createPortal(
        <StyledSystemProvider theme="workleap" colorScheme={colorScheme}>
            {toasts}
        </StyledSystemProvider>,
        document.body
    );
};
